"use client";
import { useCallback, type ComponentProps } from "react";

import { Badge, badgeVariants } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { logo } from "@/lib/logo-dev";
import { cn } from "@/lib/utils";

import type { VariantProps } from "class-variance-authority";
import { CentralIconName } from "@/types/central-icon";
import { Icon } from "@/components/ui/icon";

export type OptionBadgeVariantProps = VariantProps<typeof badgeVariants>;

export type OptionItem = {
  id: string;
  name: string;
  avatarUrl?: string;
  domain?: string;
};

export type OptionBadgeContainerProps = React.ComponentProps<typeof Badge>;

function OptionBadgeContainer({
  className,
  variant = "secondary",
  size = "sm",
  ...props
}: OptionBadgeContainerProps) {
  return (
    <Badge variant={variant} size={size} className={cn(className)} {...props} />
  );
}

export type OptionBadgeLogoProps = {
  name: string;
  domain?: string | null;
  src?: string | null;
  fallbackIcon?: CentralIconName;
  className?: string;
};

function OptionBadgeLogo({
  name,
  domain,
  src: srcProp,
  fallbackIcon = "IconBuildingBlocks",
  className,
}: OptionBadgeLogoProps) {
  const builtUrl = !srcProp ? logo({ domain, name, size: 32 }) : null;

  const url = srcProp ?? builtUrl;
  return (
    <Avatar className={cn("size-3 shrink-0 rounded-xs", className)}>
      <AvatarImage src={url} alt="" />
      <AvatarFallback className="bg-white/6">
        <Icon
          name={fallbackIcon}
          size={10}
          className="text-heading opacity-40"
        />
      </AvatarFallback>
    </Avatar>
  );
}

function OptionBadgeName({
  className,
  ...props
}: React.ComponentProps<"span">) {
  return <span className={cn(className)} {...props} />;
}

export type OptionBadgeRemoveButtonProps = {
  onClick: (e: React.MouseEvent) => void;
  "aria-label"?: string;
  className?: string;
};

function OptionBadgeRemoveButton({
  onClick,
  "aria-label": ariaLabel = "Remove",
  className,
}: OptionBadgeRemoveButtonProps) {
  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onClick(e);
    },
    [onClick],
  );

  return (
    <button
      type="button"
      aria-label={ariaLabel}
      onClick={handleClick}
      className={cn(
        "relative flex size-3 shrink-0 cursor-pointer items-center hover:opacity-80 justify-center rounded-sm transition-colors before:absolute before:inset-[-0.25rem] before:content-['']",
        className,
      )}
    >
      <Icon
        name="IconCrossMedium"
        size={14}
        className="text-contrast/40 transition-opacity"
      />
    </button>
  );
}

export const OptionBadge = {
  Container: OptionBadgeContainer,
  Logo: OptionBadgeLogo,
  Name: OptionBadgeName,
  RemoveButton: OptionBadgeRemoveButton,
};
